/* Adapter: Horizon's real ThemeEvents/StandardEvents -> the event names every
 * ported CRO module (afroyla-coupon-strip.js, afroyla-atc-price-pill.js,
 * afroyla-coupon-price-sync.js, afroyla-set-picker.js, afroyla-preorder.js)
 * already expects: `theme:variant:change` with `{detail:{variant}}`, and
 * `theme:cart:refresh` with no payload.
 *
 * Verified against the shipped Horizon version (not assumed): the real names
 * come from the `@shopify/events` import map entry, which resolves to
 * https://cdn.shopify.com/storefront/standard-events.js (see
 * snippets/scripts.liquid). That module exports StandardEvents.productSelect
 * = "shopify:product:select" and StandardEvents.cartLinesUpdate =
 * "shopify:cart:lines-update" — confirmed by reading the deployed source, the
 * same event phase-02/03 already found and used for the cart-drawer toggle.
 *
 * `shopify:product:select` bubbles on the variant-picker element with a
 * `.promise` that resolves once Horizon's own Section Rendering fetch
 * completes; the resolved value carries `variant` from the page's own
 * analytics view-payload (id/price/compare_at_price/available). This module
 * is a bridge for markup Horizon itself still renders elsewhere on the page
 * (e.g. the "You may also like" recommendations' native product cards) — the
 * PDP buy box in sections/afroyla-product.liquid is deliberately built with
 * our own light-DOM form + afroyla-material-cards.js and dispatches
 * theme:variant:change synchronously and directly, for exactly the reasons
 * given in phase-06's Risk Assessment §1/§3 (Shadow DOM + async section-morph
 * makes Horizon's native picker an unreliable single writer for a pill/
 * headline three other scripts also converge on). */
(function () {
  'use strict';

  // Theme-editor section re-renders, and the size-sibling section swap in
  // afroyla-material-cards.js, re-execute this script tag; without a guard
  // every listener below stacks a duplicate on each re-render.
  if (window.__aflThemeEventsInit) return;
  window.__aflThemeEventsInit = true;

  document.addEventListener('shopify:product:select', function (event) {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    event.promise
      .then(function (resolved) {
        var variant = resolved && resolved.variant;
        if (!variant || !variant.id) return;
        var target = event.target && event.target.nodeType === 1 ? event.target : document;
        target.dispatchEvent(
          new CustomEvent('theme:variant:change', { detail: { variant: variant }, bubbles: true })
        );
      })
      .catch(function () {
        /* Aborted or rejected fetch — nothing to bridge. */
      });
  });

  document.addEventListener('shopify:cart:lines-update', function (event) {
    // Skip events the reverse bridge below synthesized from our own
    // `theme:cart:refresh` — without this guard the two bridges ping-pong
    // forever: this listener re-dispatches theme:cart:refresh, the reverse
    // bridge answers with another synthetic shopify:cart:lines-update, which
    // lands right back here, fetching /cart.js again on every tick.
    if (event.__aflSynthetic) return;

    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.catch(function () {
        /* A failed cart mutation still deserves a refresh so stale UI
         * (loading states, optimistic counts) settles back to truth. */
      });
    }
  });

  /* Reverse bridge: theme:cart:refresh -> shopify:cart:lines-update.
   *
   * Every ported add path (afroyla-pdp-buybox.js solo submit, afroyla-set-
   * picker.js multiAdd, afroyla-pdp-upsell.js addOne, afroyla-coupon-
   * strip.js confirmClaim) still dispatches the Broadcast-shaped
   * `theme:cart:refresh` — no payload, fire-and-forget — once its own
   * /cart/add.js or /discount/{code} call resolves. Horizon's entire cart
   * chrome listens ONLY for the real `shopify:cart:lines-update`:
   *   - component-cart-items.js re-renders the drawer/page cart section,
   *     reading `event.promise` and falling back to a fresh Section
   *     Rendering fetch (`sectionRenderer.renderSection`) whenever the
   *     resolved value carries no `detail.sections` — which is always, for
   *     this bridge, since /cart.js returns no section HTML. That fallback
   *     is the intended path here, not a defect.
   *   - cart-drawer.js auto-opens the drawer only when `event.action ===
   *     'add'` and the drawer isn't already open.
   *   - afroyla-header.js / header-actions.js / cart-icon.js read the cart
   *     count off `event.promise` (`cart.totalQuantity`) with a
   *     `detail.itemCount` fallback.
   * Without this bridge those four add paths were invisible to all of the
   * above: no drawer refresh, no auto-open, no live count (code review
   * finding C1).
   *
   * No listener in this theme does `instanceof CartLinesUpdateEvent` —
   * verified by reading every StandardEvents.cartLinesUpdate /
   * 'shopify:cart:lines-update' consumer under horizon/assets — they only
   * read the plain properties documented in standard-events.d.ts
   * (`action`, `context`, `lines`, `promise`, `detail`). So this builds a
   * CustomEvent and assigns those properties directly instead of importing
   * the real class from the `@shopify/events` CDN module: same contract,
   * one fewer network dependency on the add-to-cart path.
   *
   * `action` defaults to 'add' (drawer auto-opens) for the real add paths.
   * A caller whose action is NOT an add — the collection hero's coupon
   * claim, which applies a discount to the session without touching a
   * line item — passes `detail: { action: 'update' }` so the cart chrome
   * refreshes silently instead of popping an (often empty) drawer over
   * the grid the shopper is browsing. Owner call 2026-08-26: claim must
   * never surface the cart. */
  document.addEventListener('theme:cart:refresh', function (trigger) {
    var action =
      trigger && trigger.detail && trigger.detail.action === 'update' ? 'update' : 'add';
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (ajaxCart) {
        var itemCount =
          ajaxCart && typeof ajaxCart.item_count === 'number' ? ajaxCart.item_count : undefined;

        var cart = ajaxCart
          ? {
              id: ajaxCart.token || '',
              totalQuantity: ajaxCart.item_count,
              cost: {
                totalAmount: {
                  amount: (ajaxCart.total_price / 100).toFixed(2),
                  currencyCode: ajaxCart.currency || ''
                }
              },
              lines: (ajaxCart.items || []).map(function (item) {
                return {
                  id: item.key || String(item.id),
                  quantity: item.quantity,
                  cost: {
                    totalAmount: {
                      amount: ((item.final_line_price || 0) / 100).toFixed(2),
                      currencyCode: ajaxCart.currency || ''
                    }
                  }
                };
              }),
              discountCodes: ajaxCart.discount_codes || []
            }
          : null;

        // `detail` cannot be assigned after construction — CustomEvent.prototype.detail
        // is a getter-only accessor and this file runs in strict mode, so it
        // must go through the constructor's init dict. The other properties
        // (`action`/`context`/`lines`/`promise`) are plain own-properties, safe
        // to assign directly, matching how the real CartLinesUpdateEvent class
        // exposes them.
        var evt = new CustomEvent('shopify:cart:lines-update', {
          bubbles: false,
          detail: { itemCount: itemCount }
        });
        evt.action = action;
        evt.context = 'product';
        evt.lines = [];
        evt.promise = Promise.resolve({ cart: cart, detail: { itemCount: itemCount } });
        // Marks this event as bridge-synthesized so the forward bridge above
        // does not treat it as a fresh Horizon dispatch and loop back into
        // another theme:cart:refresh (see the guard there).
        evt.__aflSynthetic = true;

        document.dispatchEvent(evt);
      });
  });
})();
